// ESA conversational API. Uses the BFF adapter for transport — the host
// doesn't know whether it's REST, GraphQL, or something else underneath.
//
// Phase 1: blocking HTTP. Track C: ReadableStream SSE upgrade.
// All functions use bffRequest() through the unified BFF (DR per design).

import { BffError, getActiveProductLineId, getProductLineDeclarations } from '../../../adapters';
import { bffRequest } from '../../../common/services/httpClient';
import { AGOPS_API_PATHS, ESA_API_PATHS } from '../../../constants/apiPaths';
import type {
  ConversationListResponse,
  ConversationMessageListResponse,
  ConverseRequest,
  ConverseResponse,
  InboxListResponse,
  InboxItem,
  AgOpsHilItem,
  AgOpsHilOptionInput,
  AgOpsHilSearchResponse,
  AgOpsHilSearchResultItem,
} from '../types';

export type { ConverseRequest, ConverseResponse };

interface GafEnvelope<T> {
  status: { status_code: number; details?: Record<string, unknown> };
  response: T;
}

/** Pull the user-facing converse message out of a GAF error envelope (or bare response). */
function extractConverseFailureMessage(err: unknown): string {
  if (err instanceof BffError) {
    const body = err.body;
    if (body && typeof body === 'object') {
      const envelope = body as Partial<GafEnvelope<ConverseResponse>> & Partial<ConverseResponse>;
      const nested = envelope.response;
      if (nested && typeof nested === 'object' && typeof nested.message === 'string' && nested.message.trim()) {
        return nested.message.trim();
      }
      if (typeof envelope.message === 'string' && envelope.message.trim()) {
        return envelope.message.trim();
      }
      if (typeof (body as { detail?: unknown }).detail === 'string') {
        return String((body as { detail: string }).detail);
      }
    }
    if (err.status === 504) {
      return 'That request timed out before the data assistant finished. Please try again.';
    }
    if (err.status === 503 || err.status === 502) {
      return "I couldn't reach the data assistant just now. Please try again in a moment.";
    }
    return `ESA request failed (${err.status} ${err.statusText}). Please try again.`;
  }
  if (err instanceof Error && err.message.trim()) {
    return err.message.trim();
  }
  return 'ESA is unavailable right now.';
}

function resolveBffTenant(): string {
  try {
    const plId = getActiveProductLineId();
    const declarations = getProductLineDeclarations();
    const pl = declarations.find(d => d.id === plId) ?? declarations[0];
    return (pl?.bff.tenant ?? pl?.auth.tenant ?? '').trim();
  } catch {
    return '';
  }
}

function unwrapGafResponse<T>(data: T | GafEnvelope<T>): T {
  if (data && typeof data === 'object' && 'response' in data && 'status' in data) {
    return (data as GafEnvelope<T>).response;
  }
  return data as T;
}

function resolveWorkscopeUkey(override?: string): string | undefined {
  const ukey = (override ?? '').trim();
  return ukey || undefined;
}

/** Map panel-friendly converse input to GAF /converse body (tenant in body, not header). */
function buildGafConverseBody(req: ConverseRequest): Record<string, unknown> {
  const workscopeUkey = resolveWorkscopeUkey(req.context?.workscope_ukey as string | undefined);
  const context: Record<string, unknown> = { ...(req.context ?? {}) };
  if (workscopeUkey) {
    context.workscope_ukey = workscopeUkey;
  }

  // turn_number is intentionally omitted — the ESA agent resolves it from
  // AgOps message history. Sending it from the client would override that.
  return {
    tenant_ukey: (req.tenant_ukey ?? resolveBffTenant()).trim(),
    user_message: req.message,
    session_id: req.session_id ?? '',
    initiator_id: req.initiator_id ?? '',
    initiator_type: 'user',
    context,
  };
}

// ─── Converse (UC-1) ──────────────────────────────────────────────────────────

/** Send a blocking message to the ESA Agent BFF. Returns the full response. */
export async function converse(req: ConverseRequest): Promise<ConverseResponse> {
  try {
    const raw = await bffRequest<GafEnvelope<ConverseResponse> | ConverseResponse>(ESA_API_PATHS.CONVERSE, {
      method: 'POST',
      body: buildGafConverseBody(req),
    });
    const response = unwrapGafResponse(raw);
    const errorCode = response.metadata?.error_code;
    if (errorCode) {
      throw new Error(response.message || `ESA converse failed (${errorCode})`);
    }
    return response;
  } catch (err) {
    // Non-2xx responses (e.g. DESO timeout → ESA 504) throw BffError before unwrap.
    // Prefer ESA's structured message over "BFF request failed: 504 Gateway Timeout".
    if (err instanceof Error && !(err instanceof BffError) && err.message.trim()) {
      throw err;
    }
    throw new Error(extractConverseFailureMessage(err));
  }
}

// ─── Conversations (UC-1 thread list) ────────────────────────────────────────

export interface CreateConversationResult {
  id: string;
  title: string | null;
}

export interface CreateConversationOptions {
  /** Must match the tenant_ukey sent in the converse() body so AgOps scope is identical. */
  tenantId?: string;
  /** Must match the initiator_id sent in the converse() body. */
  userId?: string;
  /** WorkScope to associate the conversation with. */
  workscopeUkey?: string;
}

/**
 * Explicitly create a new conversation in AgOps via the ESA Agent proxy.
 * Call this BEFORE the first converse() of a new session so the conversation
 * is guaranteed to exist in AgOps before any messages are persisted, making it
 * visible in the thread list immediately after reload.
 *
 * IMPORTANT: pass the same tenantId / userId that converse() will use — the ESA
 * agent proxy reads x-tenant-id / x-user-id headers for the create call, and
 * ensure_conversation() uses those same values to look the conversation up in AgOps.
 * A mismatch causes SESSION_NOT_FOUND.
 */
export async function createConversation(
  title?: string,
  options: CreateConversationOptions = {},
): Promise<CreateConversationResult> {
  const body: Record<string, unknown> = { conversation_type: 'esa' };
  if (title) body.title = title;
  const workscopeUkey = resolveWorkscopeUkey(options.workscopeUkey);
  if (workscopeUkey) body.workscope_ukey = workscopeUkey;

  // Pass tenant + user as explicit headers so AgOps stores the conversation
  // under the exact same scope that ensure_conversation() will look up.
  const headers: Record<string, string> = {};
  const tenant = (options.tenantId ?? resolveBffTenant()).trim();
  if (tenant) headers['x-tenant-id'] = tenant;
  if (options.userId) headers['x-user-id'] = options.userId;

  const raw = await bffRequest<GafEnvelope<CreateConversationResult> | CreateConversationResult>(
    ESA_API_PATHS.CONVERSATIONS,
    { method: 'POST', body, headers }
  );
  const data = unwrapGafResponse(raw) as unknown as Record<string, unknown>;
  return { id: String(data.id), title: (data.title as string | null) ?? null };
}

interface ListConversationsParams {
  workscope_ukey?: string;
  status?: string;
  offset?: number;
  limit?: number;
}

/** Fetch paginated conversation list from the ESA Agent proxy. */
export async function listConversations(params: ListConversationsParams = {}): Promise<ConversationListResponse> {
  const qs = new URLSearchParams();
  const workscopeUkey = resolveWorkscopeUkey(params.workscope_ukey);
  if (workscopeUkey) qs.set('workscope_ukey', workscopeUkey);
  if (params.status) qs.set('status', params.status);
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await bffRequest<GafEnvelope<ConversationListResponse> | ConversationListResponse>(
    `${ESA_API_PATHS.CONVERSATIONS}${query}`,
    { method: 'GET' }
  );
  const data = unwrapGafResponse(raw);
  // Normalise: backend may return bare array instead of paginated envelope
  if (Array.isArray(data)) {
    return { items: data as ConversationListResponse['items'], total: (data as unknown[]).length };
  }
  return data as ConversationListResponse;
}

/** PATCH /v1/conversations/{id} — update mutable fields (title, etc.) */
export async function updateConversation(
  conversationId: string,
  patch: { title?: string; metad_record_status?: string },
): Promise<void> {
  await bffRequest<unknown>(
    ESA_API_PATHS.conversation(conversationId),
    { method: 'PATCH', body: patch }
  );
}

/** Soft-delete a conversation via PATCH metad_record_status=inactive. */
export async function deleteConversation(conversationId: string): Promise<void> {
  await updateConversation(conversationId, { metad_record_status: 'inactive' });
}

/** Fetch paginated messages for a conversation (for initial load and scroll-up pagination). */
export async function getConversationMessages(
  conversationId: string,
  params: { offset?: number; limit?: number } = {}
): Promise<ConversationMessageListResponse> {
  const qs = new URLSearchParams();
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const raw = await bffRequest<GafEnvelope<ConversationMessageListResponse> | ConversationMessageListResponse>(
    `${ESA_API_PATHS.conversation(conversationId)}${query}`,
    { method: 'GET' }
  );
  const data = unwrapGafResponse(raw);
  if (Array.isArray(data)) {
    return { items: data as ConversationMessageListResponse['items'], total: (data as unknown[]).length };
  }
  return data as ConversationMessageListResponse;
}

// ─── ESA Inbox (UC-1) ────────────────────────────────────────────────────────

function mapAgOpsStatusToInbox(status: string | null | undefined): InboxItem['status'] {
  return (status ?? '').toUpperCase() === 'PENDING' ? 'open' : 'resolved';
}

// Gate-blocking HILs (context.urgency === "blocking") are always high — the
// pipeline is stuck waiting, regardless of the priority label. For everything
// else, fall back to the top-level priority field (matching EPS _URGENCY_MAP).
function mapAgOpsUrgency(
  priority: string | null | undefined,
  contextUrgency: string | null | undefined,
): InboxItem['urgency'] {
  if ((contextUrgency ?? '').toLowerCase() === 'blocking') return 'high';
  const p = (priority ?? '').toUpperCase();
  if (p === 'CRITICAL' || p === 'HIGH') return 'high';
  if (p === 'LOW') return 'low';
  return 'medium';
}

/**
 * Prefer `context.options` (structured HITLOption) over `hil_metadata.options`.
 * Returns human-readable labels for chip display and send (label if present, else value).
 */
export function normalizeHilOptionLabels(
  contextOptions: AgOpsHilOptionInput[] | null | undefined,
  metadataOptions: AgOpsHilOptionInput[] | null | undefined,
): string[] {
  const raw =
    Array.isArray(contextOptions) && contextOptions.length > 0
      ? contextOptions
      : (metadataOptions ?? []);

  const labels: string[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const text = item.trim();
      if (!text) continue;
      labels.push(text);
      continue;
    }
    if (item && typeof item === 'object') {
      const value = String(item.value ?? '').trim();
      if (!value) continue;
      const label = String(item.label ?? '').trim() || value;
      labels.push(label);
    }
  }
  return labels;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function mapHilArtifact(raw: unknown): InboxItem['artifact'] {
  const artifact = asRecord(raw);
  if (!artifact) return undefined;
  const type = String(artifact.type ?? '').trim();
  if (type !== 'table' && type !== 'chart' && type !== 'text') return undefined;
  const columns = Array.isArray(artifact.columns)
    ? artifact.columns.map(c => String(c))
    : undefined;
  const rows = Array.isArray(artifact.rows)
    ? artifact.rows
        .filter((row): row is unknown[] => Array.isArray(row))
        .map(row => row.map(cell => String(cell ?? '')))
    : undefined;
  return {
    id: String(artifact.id ?? 'hil-artifact'),
    title: String(artifact.title ?? 'Related evidence'),
    icon: String(artifact.icon ?? 'table'),
    type,
    columns,
    rows,
  };
}

export function mapHilToInboxItem(raw: AgOpsHilItem): InboxItem {
  const ctx = raw.context;
  const meta = asRecord(raw.hil_metadata);
  const conversationId = raw.conversation_id ?? null;
  const solutionId = raw.solution_agent_ukey ?? '';
  const suggestions = normalizeHilOptionLabels(ctx?.options, raw.hil_metadata?.options);
  const attentionRaw = asRecord(meta?.attention);
  const outcomeName =
    String(meta?.name ?? '').trim() ||
    String(attentionRaw?.title ?? '').trim() ||
    raw.question ||
    '';
  const attentionSummary =
    String(attentionRaw?.summary ?? '').trim() ||
    String(meta?.summary ?? '').trim() ||
    String(ctx?.summary ?? '').trim() ||
    '';
  const linkedItemTitle =
    String(meta?.linkedItemTitle ?? '').trim() ||
    String(meta?.linked_item_title ?? '').trim() ||
    null;
  const artifact =
    mapHilArtifact(ctx?.artifact) ??
    mapHilArtifact(meta?.artifact);

  return {
    id: raw.id,
    type: 'hitl',
    status: mapAgOpsStatusToInbox(raw.status),
    name: outcomeName,
    raisedAt: raw.metad_created_at,
    priority: raw.priority,
    urgency: mapAgOpsUrgency(raw.priority, ctx?.urgency),
    attention: {
      title: String(attentionRaw?.title ?? '').trim() || outcomeName,
      summary: attentionSummary,
      count: Number(attentionRaw?.count ?? 1) || 1,
      objective: String(attentionRaw?.objective ?? ''),
      solution: String(attentionRaw?.solution ?? meta?.solution_label ?? ''),
      run: String(attentionRaw?.run ?? ''),
    },
    linkedItemTitle: linkedItemTitle || null,
    suggestions,
    moduleId: raw.agent_ukey,
    solutionId,
    agentUkey: raw.agent_ukey,
    workscopeUkey: ctx?.origin?.workscope_ukey ?? '',
    conversationId,
    uiPresentation: (ctx?.ui_presentation as string | null | undefined) ?? null,
    artifact,
  };
}

// AgOps returns context and hil_metadata as Python-serialized strings (str() output).
// These helpers extract scalar and list values from those strings via targeted regex.
function extractPyStringField(s: string, key: string): string | null {
  const match = s.match(new RegExp(`'${key}':\\s*'([^']*)'`));
  return match?.[1] ?? null;
}

function extractPyListStrings(s: string, key: string): string[] {
  const match = s.match(new RegExp(`'${key}':\\s*\\[([^\\]]+)\\]`));
  if (!match) return [];
  return match[1]
    .split(',')
    .map(v => v.trim().replace(/^'|'$/g, ''))
    .filter(Boolean);
}

export function mapHilSearchToInboxItem(raw: AgOpsHilSearchResultItem): InboxItem {
  const d = raw.data;
  const ctx = d.context ?? '';
  const meta = d.hil_metadata ?? '';

  const urgency = extractPyStringField(ctx, 'urgency');
  const workscopeUkey = extractPyStringField(ctx, 'workscope_ukey') ?? '';
  // solution_agent_ukey may appear under different keys across schema versions
  const solutionId =
    extractPyStringField(meta, 'solution_agent_ukey') ??
    extractPyStringField(meta, 'solution_ukey') ??
    '';
  // Prefer context.options when present (even as plain strings in the py-repr).
  // Structured {value,label} dicts are only available via getHilItem → mapHilToInboxItem.
  const contextSuggestions = extractPyListStrings(ctx, 'options');
  const metadataSuggestions = extractPyListStrings(meta, 'options');
  const suggestions =
    contextSuggestions.length > 0 ? contextSuggestions : metadataSuggestions;
  // ui_presentation is markdown stored in the context string; extract if present
  const uiMatch = ctx.match(/'ui_presentation':\s*'([^']+)'/);
  const uiPresentation = uiMatch?.[1]?.replace(/\\n/g, '\n') ?? null;

  const metaName = extractPyStringField(meta, 'name');
  const attentionTitle = extractPyStringField(meta, 'title');
  const attentionSummary =
    extractPyStringField(meta, 'summary') ??
    extractPyStringField(ctx, 'summary') ??
    '';
  const linkedItemTitle =
    extractPyStringField(meta, 'linkedItemTitle') ??
    extractPyStringField(meta, 'linked_item_title');
  const outcomeName = metaName || attentionTitle || d.question || '';

  return {
    id: d.id,
    type: 'hitl',
    status: mapAgOpsStatusToInbox(d.status),
    name: outcomeName,
    raisedAt: d.metad_created_at,
    priority: d.priority,
    urgency: mapAgOpsUrgency(d.priority, urgency),
    attention: {
      title: attentionTitle || outcomeName,
      summary: attentionSummary,
      count: 1,
      objective: '',
      solution: extractPyStringField(meta, 'solution_label') ?? '',
      run: '',
    },
    linkedItemTitle,
    suggestions,
    moduleId: d.agent_ukey,
    solutionId,
    agentUkey: d.agent_ukey,
    workscopeUkey,
    conversationId: d.conversation_id ?? null,
    uiPresentation,
  };
}

interface HilSearchClause {
  field: string;
  values: (string | boolean | number)[];
}

interface HilSearchPagination {
  limit?: number;
  after_ukey?: string;
}

interface HilSearchSelect {
  fields: string[];
}

interface HilSearchBody {
  search_mode: 'structured';
  where: HilSearchClause[];
  pagination?: HilSearchPagination;
  select?: HilSearchSelect;
}

const HIL_SEARCH_FIELDS: string[] = [
  'id',
  'question',
  'agent_run_id',
  'conversation_id',
  'agent_ukey',
  'request_type',
  'priority',
  'status',
  'metad_created_at',
];

/**
 * Fetch HIL requests via the AgOps structured search endpoint.
 * Calls POST {bffBaseUrl}/services/ops/hil-requests/search with a structured
 * where-clause body and maps the snake_case response to InboxItem.
 */
export async function fetchInboxRequests(params: {
  status?: string;
  limit?: number;
  after_ukey?: string;
  has_conversation?: boolean;
}): Promise<InboxListResponse> {
  const where: HilSearchClause[] = [
    { field: 'status', values: [params.status ?? 'PENDING'] },
  ];
  if (params.has_conversation !== undefined) {
    where.push({ field: 'has_conversation', values: [params.has_conversation] });
  }

  const pagination: HilSearchPagination = {};
  if (params.limit !== undefined) pagination.limit = params.limit;
  if (params.after_ukey) pagination.after_ukey = params.after_ukey;

  const body: HilSearchBody = {
    search_mode: 'structured',
    where,
    ...(Object.keys(pagination).length > 0 && { pagination }),
    select: { fields: HIL_SEARCH_FIELDS },
  };

  const raw = await bffRequest<AgOpsHilSearchResponse>(
    AGOPS_API_PATHS.HIL_SEARCH,
    { method: 'POST', body }
  );
  const lastConceptUkey =
    raw.items.length > 0 ? raw.items[raw.items.length - 1].concept_ukey : null;
  const pageLimit = params.limit ?? 0;
  const resolvedAfterUkey = raw.cursor?.after_ukey ?? lastConceptUkey;
  const resolvedHasMore =
    raw.items.length === 0
      ? false
      : raw.cursor?.has_more ?? raw.items.length >= pageLimit;

  return {
    items: raw.items.map(mapHilSearchToInboxItem),
    total: raw.total,
    offset: 0,
    limit: pageLimit,
    degraded: false,
    summary: { pending: 0, approved: 0, rejected: 0 },
    cursor: {
      after_ukey: resolvedAfterUkey,
      has_more: resolvedHasMore,
    },
  };
}

// ─── HITL Resolution (UC-2, DR-14) ───────────────────────────────────────────

/**
 * Fetch a single HIL request by ID.
 * Used after each converse turn to check whether the agent resolved the HIL.
 */
export async function getHilItem(hilId: string): Promise<AgOpsHilItem> {
  const raw = await bffRequest<GafEnvelope<AgOpsHilItem> | AgOpsHilItem>(
    AGOPS_API_PATHS.hilById(hilId),
    { method: 'GET' }
  );
  return unwrapGafResponse(raw);
}
